import { Box, Button, Card, CircularProgress, Divider, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { RHFAutocomplete, RHFTextField } from '@/commons/hook-form';
import { Iconic } from '@/commons/iconic';
import { Product } from '@/modules/products/types';
import { getProductsResumeService } from '@/services';
import { translates } from '@/utils';
import { useItemList } from '../../hooks/useItemsList';

export type AutocompleteLabels = Record<string, any>;
type Props = {
  isEdit?: boolean;
  isForm?: boolean;
  defaultValuesItems?: any;
  id?: string;
};

export const ProductItemsListOrder = ({ isEdit, defaultValuesItems, id, isForm }: Props) => {
  const { isLoading, data } = useQuery({
    queryKey: ['products'],
    queryFn: getProductsResumeService,
  });
  const { fields, handleAdd, handleChangeQuantity, handleRemove } = useItemList({
    isEdit,
    defaultValuesItems,
  });

  if (isLoading) {
    return <CircularProgress size={48} color="info" />;
  }
  return (
    <Card sx={{ p: isForm ? 3 : 0 }}>
      <h1>{id}</h1>
      <Stack spacing={3}>
        <Box sx={{ p: 3 }}>
          <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={3}>
            {fields.map((item, index) => (
              <Stack key={item.id} alignItems="flex-end" spacing={1.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: 1 }}>
                  <RHFAutocomplete
                    disabled={isEdit}
                    name={`items[${index}].product`}
                    size="small"
                    label={translates.products}
                    options={data?.data?.map?.((itemTemp: Product) => ({
                      value: itemTemp.product.id,
                      label: itemTemp.product?.name,
                    })) ?? []}
                    isOptionEqualToValue={(
                      option: AutocompleteLabels,
                      selected: AutocompleteLabels
                    ) => option.value === selected.value}
                    fullWidth
                  />

                  <RHFTextField
                    size="small"
                    type="number"
                    name={`items[${index}].quantity`}
                    label={translates.quantity}
                    placeholder="0"
                    defaultValue={1}
                    onChange={(event) => handleChangeQuantity(event, index)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputProps: {
                        min: 1,
                      },
                    }}
                    sx={{ maxWidth: { md: 130 } }}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: 1 }}>
                  <RHFTextField
                    size="small"
                    name={`items[${index}].description`}
                    placeholder={translates.description}
                    InputLabelProps={{ shrink: true }}
                    style={{ minWidth: '100%' }}
                  />
                </Stack>

                <Button
                  size="small"
                  color="error"
                  startIcon={<Iconic icon="eva:trash-2-outline" />}
                  onClick={() => handleRemove(index)}
                >
                  {translates.remove}
                </Button>
              </Stack>
            ))}
          </Stack>
          {!isEdit ? (
            <Stack
              spacing={2}
              direction={{ xs: 'column-reverse', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Button
                size="small"
                startIcon={<Iconic icon="eva:plus-fill" />}
                onClick={handleAdd}
                sx={{ flexShrink: 0 }}
              >
                {translates.add_product}
              </Button>
            </Stack>
          ): <div />}
        </Box>
      </Stack>
    </Card>
  );
};
